<template>
	<div
		class="data-container"
		ref="containerRef"
		:style="containerTransformStyle"
	>
		<!-- ▸ BREADCRUMB -->
		<header id="data-chart" class="breadcrumb">
			<div class="breadcrumb-left">
				<span class="device-name">{{ deviceName || "Device" }}</span>
				<span class="device-id-badge">{{ deviceId }}</span>
				<StatusDot :value="computedStatus" class="status-indicator" />

				<div class="header-meta-group" v-if="lastUpdate">
					<span class="meta-label">Ultimo agg.</span>
					<span class="meta-value">{{ formatFullDate(lastUpdate) }}</span>
					<span class="delay-badge" v-if="getDelayString(lastUpdate)">
						Ritardo {{ getDelayString(lastUpdate) }}
					</span>
					<!-- Tracker Battery -->
					<span class="battery-badge" v-if="trackerBattery !== null">
						Batt. {{ trackerBattery }}%
					</span>
				</div>
			</div>

			<div class="breadcrumb-actions">
				<ViewToggle id="toggle-button" class="toggle-icon" />
				<!-- <button
          v-if="isStatic"
          class="btn-config-charts"
          @click="showChartConfig = true"
          title="Configura Grafici"
        >
          <FontAwesomeIcon :icon="faChartBar" />
        </button> -->
				<NotificationBell
					v-if="showCrumbBell"
					placement="header"
				/>
				<button
					id="exit-to-devices"
					class="btn-exit"
					@click="router.push('/devices')"
				>
					Esci
				</button>
			</div>
		</header>

		<!-- ▸ CONTENUTO PAGG (Senza Tab Header) -->
		<div class="content-wrapper">
			<keep-alive>
				<RecordsTab
					v-if="isStatic"
					:device-id="deviceId"
					:headless="true"
					:device-name="deviceName"
					:current-location="deviceLoc"
					:current-temp-c="currentTempC"
					:outside-hum="outsideHum"
					:last-update-ago="lastUpdateAgo"
					:chart-range-met="chartRange"
					:series-t="seriesT"
					:series-h="seriesH"
					:temp-min="tempMin"
					:temp-max="tempMax"
					:hum-min="humMin"
					:hum-max="humMax"
					:meteo-loading="meteoLoading"
					@refresh="manualRefresh"
					@center="centerOnMarker"
					@copy-coords="copyCoords"
					@update:range-met="loadMeteoHistory"
				/>

				<VehicleTab
					v-else
					:headless="true"
					:current-temp-c="currentTempC"
					:outside-hum="outsideHum"
					:last-update-ago="lastUpdateAgo"
					:chart-range-met="chartRange"
					:series-t="seriesT"
					:series-h="seriesH"
					:temp-min="tempMin"
					:temp-max="tempMax"
					:hum-min="humMin"
					:hum-max="humMax"
					:meteo-loading="meteoLoading"
					@refresh="manualRefresh"
					@center="centerOnMarker"
					@copy-coords="copyCoords"
					@update:range="loadMeteoHistory"
					@location-update="onLocationUpdate"
					@timestamp-update="onTimestampUpdate"
				/>
			</keep-alive>
		</div>

		<!-- Chart Visibility Config Modal -->
		<!-- <ChartVisibilityModal
      v-if="showChartConfig"
      :device-type="isStatic ? 'static' : 'dynamic'"
      @close="showChartConfig = false"
    /> -->
	</div>
</template>

<script setup lang="ts">
/* ───── imports ───── */
import axios from "@/axiosInstance";
import router from "@/router";
import {
	computed,
	inject,
	onBeforeUnmount,
	onMounted,
	ref,
	shallowRef,
	triggerRef,
	watch,
	type Ref,
} from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { API_URL } from "@/config";

import NotificationBell from "@/components/NotificationBell.vue";
import ViewToggle from "@/components/ViewToggle.vue";
import StatusDot from "@/components/ui/StatusDot.vue";

// Tabs
import RecordsTab from "@/components/MultiChartsPage.vue";
import VehicleTab from "@/components/TelemetryPage.vue";
// import ChartVisibilityModal from "@/components/data/ChartVisibilityModal.vue";
// import { useChartVisibility } from "@/composables/useChartVisibility";
// import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
// import { faChartBar } from "@fortawesome/free-solid-svg-icons";

/* ───────── context per campanella ───────── */
const toast = useToast();
const route = useRoute();
const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;

const showCrumbBell = computed(
	() => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value),
);

/* ───────── Fake Transform Animation ───────── */
const containerRef = ref<HTMLElement | null>(null);
const containerTransformStyle = ref<{ transform?: string }>({});

// Constants for sidebar widths
const SIDEBAR_OPEN_WIDTH = 220;
const SIDEBAR_CLOSED_WIDTH = 60;
const SIDEBAR_DIFF = SIDEBAR_OPEN_WIDTH - SIDEBAR_CLOSED_WIDTH; // 160px

// Watch sidebar state and apply compensating transform
watch(isSidebarOpen, (newOpen, oldOpen) => {
	// Only apply on desktop where width changes
	if (!isDesktop.value) return;
	// Skip initial undefined -> value transition
	if (oldOpen === undefined) return;

	// Calculate the compensating offset
	// When opening (false -> true): container moves RIGHT, so we start with translateX(-160px) and animate to 0
	// When closing (true -> false): container moves LEFT, so we start with translateX(160px) and animate to 0
	const offsetPx = newOpen ? -SIDEBAR_DIFF : SIDEBAR_DIFF;

	// Step 1: Immediately apply compensating transform (no transition)
	if (containerRef.value) {
		containerRef.value.style.transition = "none";
		containerRef.value.style.transform = `translateX(${offsetPx}px)`;

		// Force reflow to apply the no-transition change
		void containerRef.value.offsetHeight;

		// Step 2: Re-enable transition and animate to 0
		requestAnimationFrame(() => {
			if (containerRef.value) {
				containerRef.value.style.transition = "transform 0.25s ease";
				containerRef.value.style.transform = "translateX(0)";
			}
		});
	}
});

/* ───────── ID e stato base ───────── */
const props = defineProps<{ deviceId?: string | number }>();
const deviceId = computed(() => {
	const val = props.deviceId || route.params.deviceId;
	const n = Number(val);
	return Number.isNaN(n) ? 0 : n;
});
const deviceName = ref("");
const status = ref<"online" | "offline" | "maintenance" | "error">("offline");
const isStatic = ref(false); // 👈 nuova ref per la visibilità del tab

/* ───────── meteo ref ───────── */
const meteoSource = ref("fallback");
const chartRange = ref("24h");
const meteoLoading = ref(false);
// const showChartConfig = ref(false);

// Chart visibility (singleton state)
// const chartVisibility = useChartVisibility();

/* ───────── posizione & modale ───────── */
const deviceLoc = ref<[number, number] | undefined>(undefined);
const showCoordModal = ref(false);

function openCoordModal() {
	showCoordModal.value = true;
}
function closeCoordModal() {
	showCoordModal.value = false;
}

async function copyCoords() {
	if (!deviceLoc.value) return;
	const txt = `${deviceLoc.value[0].toFixed(6)}, ${deviceLoc.value[1].toFixed(
		6,
	)}`;

	try {
		await navigator.clipboard.writeText(txt);
		toast.success("Coordinate copiate!", { position: "top-right" });
	} catch {
		toast.error("Impossibile copiare le coordinate.", {
			position: "top-right",
		});
	}
}

function centerOnMarker() {
	toast.success("Mappa centrata sulla posizione del device.", {
		position: "top-right",
	});
}

/* ───────── telemetria/stat ───────── */
const outsideTemp = ref(84);
const outsideHum = ref(62);
const lastUpdate = ref(Date.now());
const trackerBattery = ref<number | null>(null);

// Thresholds
const tempMin = ref<number | undefined>(undefined);
const tempMax = ref<number | undefined>(undefined);
const humMin = ref<number | undefined>(undefined);
const humMax = ref<number | undefined>(undefined);

const lastUpdateAgo = computed(() => {
	const sec = Math.max(0, Math.round((Date.now() - lastUpdate.value) / 1000));
	return sec < 60 ? `${sec}s ago` : `${Math.round(sec / 60)} min ago`;
});

// Helper DATA ORA
function formatFullDate(ts: number) {
	if (!ts) return "N/D";
	return new Date(ts).toLocaleString("it-IT", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

// Helper RITARDO [giorni ore minuti]
function getDelayString(ts: number) {
	if (!ts) return "";
	const diff = Math.max(0, Date.now() - ts);

	// Hide if < 1 minute
	if (diff < 60000) return "";

	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
	const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

	const parts = [];
	if (days > 0) parts.push(`${days}g`);
	if (hours > 0) parts.push(`${hours}h`);
	if (minutes > 0 || parts.length > 0) parts.push(`${minutes}min`);

	return parts.join(" ");
}

// COMPUTED STATUS (LED)
const computedStatus = computed(() => {
	// Se è maintenance o error esplicito, vince quello (ROSSO)
	if (
		(status.value as any) === "maintenance" ||
		(status.value as any) === "error"
	)
		return "error"; // Mappa maintenance/error -> RED (error nel componente StatusDot o maintenance se supportato)

	// Controlliamo il delay
	const last = Number(lastUpdate.value);
	if (!last || isNaN(last)) return "offline";

	const diff = Date.now() - last;
	const isDelay = diff > 15 * 60 * 1000; // 15 min

	if (
		(status.value as any) === "maintenance" ||
		(status.value as any) === "error"
	)
		return "error";

	if (isDelay) return "maintenance"; // Arancione (User request: delay > 15min -> arancione)

	// Se < 15min e stato non è error
	return "online"; // Verde
});

/* precisioni */
const TEMP_PRECISION = 1;
const HUM_PRECISION = 0;

function floorToDecimals(n: number, d = 1) {
	const k = Math.pow(10, d);
	return Math.floor(n * k) / k;
}

const formatTempC = (n: number) =>
	`${floorToDecimals(n, TEMP_PRECISION).toFixed(TEMP_PRECISION)}°C`;

const formatHum = (n: number) => `${Math.floor(n).toFixed(HUM_PRECISION)}%`;

const tickTempC = (v: number) =>
	floorToDecimals(v, TEMP_PRECISION).toFixed(TEMP_PRECISION);

const tickHumPct = (v: number) => Math.floor(v).toFixed(HUM_PRECISION);

/* ───────── serie grafico ───────── */
const seriesT = shallowRef<{ x: number; y: number }[]>([]);
const seriesH = shallowRef<{ x: number; y: number }[]>([]);

function pushSeries(t: number, h: number) {
	const now = Date.now();
	// Se l'ultimo punto è troppo vicino (< 1 minuto), sovrascrivilo o ignora
	// Per semplicità qui aggiungiamo sempre, Chart.js gestisce bene i punti
	seriesT.value.push({ x: now, y: Number(t.toFixed(2)) });
	seriesH.value.push({ x: now, y: Number(h.toFixed(2)) });

	triggerRef(seriesT);
	triggerRef(seriesH);
}

/* ───────── fallback TH ───────── */
function fallbackTH() {
	const rnd = (min: number, max: number, d = 0) =>
		Number((min + Math.random() * (max - min)).toFixed(d));
	return { temperature: rnd(18, 32, 1), humidity: Math.round(rnd(35, 75)) };
}

/* ───────── temp correnti ───────── */
const currentTempC = ref<number>(22);

function cToF(c: number) {
	return Math.round((c * 9) / 5 + 32);
}

function syncStatWithCurrentC() {
	outsideTemp.value = cToF(currentTempC.value);
}

/* ───────── polling ───────── */
const STATUS_REFRESH_MS = Number(process.env.VUE_STATUS_REFRESH_MS) || 30000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

/* ───────── API ───────── */
/* ───────── API ───────── */
/* ───────── API ───────── */
async function fetchDeviceGraph(id: number) {
	const { data } = await axios.get(`/devices/${id}/get-graph`);
	return data;
}

// Stato per VIN
const selectedVin = ref<string>("");

// Fetch telemetry logs (copiato e adattato da TelemetryPage)
async function fetchTelemetryLogs(id: number, vin: string) {
	try {
		const { data } = await axios.get(`/track/${id}/logs`, {
			params: {
				limit: 1,
				vin: vin,
				orderBy: "timestamp",
				orderDirection: "DESC",
			},
		});
		// data.logs è l'array
		if (data?.logs && Array.isArray(data.logs) && data.logs.length > 0) {
			return data.logs[0];
		}
	} catch (e) {
		console.warn("Telemetry logs fetch failed:", e);
	}
	return null;
}

// In data.vue, nella sezione script
// Aggiungi questa ref per la fonte dati

// Handler per ricevere location dal percorso caricato
function onLocationUpdate(location: [number, number]) {
	console.log("[Data.vue] Received location from route:", location);
	deviceLoc.value = location;

	// Carica i dati meteo con la nuova location
	if (deviceLoc.value) {
		loadMeteoHistory(chartRange.value);
	}
}

// Handler per ricevere timestamp dal percorso caricato
function onTimestampUpdate(timestamp: number) {
	console.log("[Data.vue] Received timestamp from TelemetryPage:", timestamp);
	// Converti il timestamp (potrebbe essere in secondi o millisecondi)
	const ts = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
	lastUpdate.value = ts;
	console.log(
		"[Data.vue] Updated header timestamp to:",
		new Date(ts).toLocaleString("it-IT"),
	);
}

// Sostituisci/modifica fetchTHFromServer:
async function loadMeteoHistory(range: string = "24h") {
	meteoLoading.value = true;
	chartRange.value = range;
	console.log(
		"[Data.vue] loadMeteoHistory called with range:",
		range,
		"deviceLoc:",
		deviceLoc.value,
	);

	if (!deviceLoc.value) {
		console.warn("[Data.vue] No deviceLoc available, skipping meteo load");
		return;
	}

	const [lat, lon] = deviceLoc.value;
	let days = 1;
	const now = new Date();

	switch (range) {
		case "24h":
			days = 1;
			break;
		case "7d":
			days = 7;
			break;
		case "30d":
			days = 30;
			break;
		default:
			days = 1;
	}

	try {
		const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m&hourly=temperature_2m,relative_humidity_2m&past_days=${days}&forecast_days=1&timezone=auto&temperature_unit=celsius`;

		console.log("[Data.vue] Fetching meteo data from:", url);
		const response = await fetch(url);
		if (!response.ok) throw new Error("OpenMeteo fetch failed");

		const data = await response.json();
		console.log(
			"[Data.vue] Meteo data received, hourly points:",
			data.hourly?.time?.length,
		);

		// Aggiorna temperatura e umidità correnti dai dati "current"
		if (data.current) {
			currentTempC.value = Number(data.current.temperature_2m) || 22;
			outsideHum.value = Number(data.current.relative_humidity_2m) || 50;
			console.log(
				"[Data.vue] Updated current values - Temp:",
				currentTempC.value,
				"Humidity:",
				outsideHum.value,
			);
			syncStatWithCurrentC();
		}

		if (data.hourly && data.hourly.time) {
			const times = data.hourly.time;
			const temps = data.hourly.temperature_2m;
			const hums = data.hourly.relative_humidity_2m;

			const newT: { x: number; y: number }[] = [];
			const newH: { x: number; y: number }[] = [];

			const cutoff = new Date();
			if (range === "24h") cutoff.setHours(cutoff.getHours() - 24);
			else if (range === "7d") cutoff.setDate(cutoff.getDate() - 7);
			else if (range === "30d") cutoff.setDate(cutoff.getDate() - 30);

			// Downsampling step: per 30d usiamo passo 4 (ogni 4 ore)
			const step = range === "30d" ? 4 : 1;

			for (let i = 0; i < times.length; i += step) {
				const tDate = new Date(times[i]);
				if (tDate >= cutoff && tDate <= now) {
					if (temps[i] !== null) newT.push({ x: tDate.getTime(), y: temps[i] });
					if (hums[i] !== null) newH.push({ x: tDate.getTime(), y: hums[i] });
				}
			}

			console.log(
				"[Data.vue] Processed meteo data - Temp points:",
				newT.length,
				"Humidity points:",
				newH.length,
			);

			// Se non abbiamo serie storiche da telemetria, usiamo meteo?
			// Ma ora preferiamo telemetria. Lasciamo questo per il grafico "meteo" se serve.
			seriesT.value = newT;
			seriesH.value = newH;
			triggerRef(seriesT);
			triggerRef(seriesH);

			console.log("[Data.vue] seriesT and seriesH updated successfully");
		}
	} catch (e) {
		console.error("[Data.vue] Meteo history load failed:", e);
	} finally {
		meteoLoading.value = false;
	}
}

// Nuova funzione leggera per il polling solo dei dati attuali
async function fetchCurrentMeteo() {
	if (!deviceLoc.value) return undefined;
	const [lat, lon] = deviceLoc.value;

	try {
		// Richiediamo SOLO `current`, niente `hourly` o `past_days` -> leggerissimo
		const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m&timezone=auto&temperature_unit=celsius`;

		const response = await fetch(url);
		if (!response.ok) throw new Error("OpenMeteo current fetch failed");

		const data = await response.json();
		if (data.current) {
			return {
				temperature: data.current.temperature_2m,
				humidity: data.current.relative_humidity_2m,
			};
		}
	} catch (e) {
		console.warn("Current Meteo fetch failed:", e);
	}
	return undefined;
}

// Funzione per salvare i dati meteo (opzionale)
async function saveMeteoData(meteoData: any) {
	try {
		await axios.post(`${API_URL}/telemetry/meteo`, meteoData);
	} catch (error) {
		console.warn("Could not save meteo data:", error);
	}
}


/* ───────── refresh tick ───────── */
async function refreshTick(id: number) {
	try {
		// 1. Fetch metadata device (nome, status)
		const graph = await fetchDeviceGraph(id);
		deviceName.value = graph?.name ?? deviceName.value;
		status.value = graph?.status ?? "offline";
		isStatic.value = graph?.is_static ?? isStatic.value;

		// Update Thresholds
		tempMin.value = graph?.temp_min !== null ? graph?.temp_min : undefined;
		tempMax.value = graph?.temp_max !== null ? graph?.temp_max : undefined;
		humMin.value = graph?.hum_min !== null ? graph?.hum_min : undefined;
		humMax.value = graph?.hum_max !== null ? graph?.hum_max : undefined;

		// Gestione VIN da track_sat_data
		const tracks = graph?.track_sat_data || [];
		if (tracks.length > 0 && !selectedVin.value) {
			// Seleziona il primo VIN se non ne abbiamo uno
			selectedVin.value = tracks[0].vin;
		}

		// 2. Fetch ultimo dato telemetrico REALE dal sistema di tracking
		let measurement = null;
		if (selectedVin.value) {
			measurement = await fetchTelemetryLogs(id, selectedVin.value);
		}

		// RIMOSSA PRIORITÀ LOCATION LIVE - TORNATI AI LOG (User Request)
		if (measurement && measurement.latitude && measurement.longitude) {
			deviceLoc.value = [measurement.latitude, measurement.longitude];
		} else if (Array.isArray(graph?.location) && graph.location.length === 2) {
			// Fallback
			deviceLoc.value = graph.location;
		}

		if (measurement) {
			// Aggiorna timestamp
			if (measurement.timestamp) {
				const ts =
					typeof measurement.timestamp === "string"
						? parseInt(measurement.timestamp, 10) * 1000 // Spesso i timestamp API sono in secondi
						: measurement.timestamp;

				const finalTs = ts < 10000000000 ? ts * 1000 : ts;

				console.log("[Data.vue] Updating lastUpdate from measurement:", {
					raw: measurement.timestamp,
					parsed: ts,
					final: finalTs,
					formatted: new Date(finalTs).toLocaleString("it-IT"),
				});

				if (!isNaN(finalTs)) lastUpdate.value = finalTs;
			} else {
				console.warn("[Data.vue] No timestamp in measurement:", measurement);
			}

			// Aggiorna batteria
			if (
				measurement.tracker_battery !== undefined &&
				measurement.tracker_battery !== null
			) {
				trackerBattery.value = measurement.tracker_battery;
			}

			// Aggiorna Temp/Hum prendendo i dati METEO della posizione (come richiesto)
			const meteo = await fetchCurrentMeteo();
			if (meteo) {
				currentTempC.value = Number(meteo.temperature);
				syncStatWithCurrentC();
				outsideHum.value = Number(meteo.humidity);

				pushSeries(currentTempC.value, outsideHum.value);
			}
		}
	} catch (err) {
		console.error("Refresh tick failed", err);
	}
}

function startPolling(id: number) {
	stopPolling();
	refreshTick(id);
	pollTimer = setInterval(() => refreshTick(id), STATUS_REFRESH_MS);
}

function stopPolling() {
	if (pollTimer) {
		clearInterval(pollTimer);
		pollTimer = null;
	}
}

/* ───────── External Static Position (Moak) ───────── */
async function fetchExternalStaticPositions() {
	try {
		// [FIX] Use backend proxy to avoid CORS
		const res = await axios.get("/haystack/static-positions", {
			withCredentials: true,
		});
		return res.data;
	} catch (e) {
		console.warn("External static positions fetch failed", e);
		return null;
	}
}

async function matchExternalStaticPosition() {
	// Solo per device statici
	if (!isStatic.value) return;

	const allPositions = await fetchExternalStaticPositions();
	if (!allPositions) return;

	// Potenziali ID da cercare: Name, VIN, Serial
	// L'utente ha indicato nomi come "1PYFKY", "XJZ1XD"
	const candidates = [deviceName.value, selectedVin.value].filter(Boolean); // Rimuovi null/empty

	let matchData = null;

	// Cerca un match diretto nei dati esterni
	for (const id of candidates) {
		if (allPositions[id]) {
			matchData = allPositions[id];
			break;
		}

		// Tentativo match parziale? (es. se nome è "Tag 1PYFKY")
		// Se le chiavi del server sono ID brevi, cerchiamo se sono contenute nel nostro nome
		const partialMatch = Object.keys(allPositions).find((k) => id.includes(k));
		if (partialMatch) {
			matchData = allPositions[partialMatch];
			break;
		}
	}

	// FALLBACK TEMPORANEO: Se non troviamo match specifico, ma c'è UN SOLO dato nel file, usiamo quello?
	// No, ora supportiamo multi-device, quindi il fallback "blind" è rischioso.
	// Ma l'utente vuole vedere 1PYFKY, XJZ1XD, B0C99B.
	// Se "deviceName" è esattamente uno di questi, funzionerà.

	if (matchData && matchData.latitude && matchData.longitude) {
		deviceLoc.value = [matchData.latitude, matchData.longitude];

		// Use external timestamp if available
		if (matchData.timestamp) {
			lastUpdate.value = matchData.timestamp;
		} else {
			// Fallback
			lastUpdate.value = Date.now();
		}

		console.log(
			"[Data.vue] Updated static position from external source:",
			matchData,
		);
	}
}

function manualRefresh() {
	refreshTick(deviceId.value);
	loadMeteoHistory(chartRange.value);
	matchExternalStaticPosition();
}

/* ───────── lifecycle ───────── */
onMounted(async () => {
	const id = deviceId.value;

	if (!id) {
		toast.error("ID dispositivo non valido", { position: "top-right" });
		router.replace("/devices");
		return;
	}

	try {
		const graph = await fetchDeviceGraph(id);

		deviceName.value = graph?.name ?? "";
		status.value = graph?.status ?? "offline";
		isStatic.value = graph?.is_static ?? false; // 👈 Salva lo stato statico

		// Update Thresholds
		tempMin.value = graph?.temp_min !== null ? graph?.temp_min : undefined;
		tempMax.value = graph?.temp_max !== null ? graph?.temp_max : undefined;
		humMin.value = graph?.hum_min !== null ? graph?.hum_min : undefined;
		humMax.value = graph?.hum_max !== null ? graph?.hum_max : undefined;

		deviceLoc.value = Array.isArray(graph?.location)
			? graph.location
			: undefined;

		console.log("[Data.vue] Initial deviceLoc from graph:", deviceLoc.value);

		// Se non abbiamo location dal graph, proviamo a prenderla dall'ultimo dato di telemetria
		// Se non abbiamo location dal graph, proviamo a prenderla dall'ultimo dato di telemetria
		if (!deviceLoc.value) {
			try {
				// Usa endpoint logs invece di latest-measurement (che dava 404)
				const logsRes = await axios.get(`/track/${id}/logs`, {
					params: { limit: 1, orderBy: "timestamp", orderDirection: "DESC" },
				});
				const latestLog = logsRes.data?.logs?.[0];

				if (latestLog?.latitude && latestLog?.longitude) {
					deviceLoc.value = [latestLog.latitude, latestLog.longitude];
					// Anche aggiorna il timestamp se possibile
					if (latestLog.timestamp) {
					}
					console.log(
						"[Data.vue] Got deviceLoc from latest log:",
						deviceLoc.value,
					);
				}
			} catch (e) {
				console.warn("[Data.vue] Could not fetch latest log for location:", e);
			}
		}

		// EXTERNAL POSITION CHECK
		await matchExternalStaticPosition();

		// Se ancora non abbiamo location, usa coordinate di Siracusa come fallback
		if (!deviceLoc.value) {
			deviceLoc.value = [37.0755, 15.2866]; // Via del Pellicano, Siracusa
			console.log(
				"[Data.vue] Using fallback location (Siracusa):",
				deviceLoc.value,
			);
		}
	} finally {
		// ... (rest of finally block)
		// Proviamo subito a prendere dati meteo reali se abbiamo la location
		if (deviceLoc.value) {
			console.log(
				"[Data.vue] Loading meteo history for location:",
				deviceLoc.value,
			);
			try {
				await loadMeteoHistory(chartRange.value);
				// non serve assegnare th, loadMeteoHistory aggiorna state
			} catch (e) {
				console.error("[Data.vue] Error loading meteo history:", e);
			}
		} else {
			console.warn(
				"[Data.vue] No location available for meteo data - charts will be empty",
			);
		}


		startPolling(id);
		syncStatWithCurrentC();
	}
});

onBeforeUnmount(stopPolling);

watch(
	() => deviceId.value,
	(newId) => {
		if (newId) {
			manualRefresh();
			startPolling(newId);
		}
	},
);
</script>

<style scoped src="@/assets/css/data.css"></style>
